// Top-bar search: find a student by name, roll/admission number, or their
// guardian's name or phone, and jump straight to the student profile.
import { useEffect, useRef, useState } from 'react';
import { Search } from 'lucide-react';
import { searchStudents } from '../api/client';

export default function GlobalSearch({ onOpenStudent }) {
  const [q,       setQ]       = useState('');
  const [results, setResults] = useState([]);
  const [open,    setOpen]    = useState(false);
  const [cursor,  setCursor]  = useState(0);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setResults([]); return; }
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        setResults(await searchStudents(term));
        setCursor(0);
      } catch {
        setResults([]);
      } finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(timer);
  }, [q]);

  useEffect(() => {
    const close = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  function pick(student) {
    onOpenStudent(student.id);
    setQ(''); setResults([]); setOpen(false);
  }

  function onKeyDown(e) {
    if (e.key === 'ArrowDown') { e.preventDefault(); setCursor(c => Math.min(c + 1, results.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setCursor(c => Math.max(c - 1, 0)); }
    else if (e.key === 'Enter' && results[cursor]) { e.preventDefault(); pick(results[cursor]); }
    else if (e.key === 'Escape') { setOpen(false); e.currentTarget.blur(); }
  }

  const showPanel = open && q.trim().length >= 2;

  return (
    <div ref={boxRef} className="relative w-full max-w-[420px]">
      <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-soft pointer-events-none" />
      <input
        value={q}
        onChange={e => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        placeholder="Search student, roll no. or parent phone"
        aria-label="Search students"
        className="w-full border border-line rounded-full pl-9 pr-3 py-2 text-sm bg-paper text-ink
          focus:outline-none focus:ring-2 focus:ring-transit focus:bg-paper-raised"
      />
      {showPanel && (
        <div className="absolute left-0 right-0 top-full mt-1.5 bg-paper-raised border border-line rounded-m shadow-2 z-[1200] overflow-hidden">
          {loading && !results.length && <div className="px-4 py-3 text-sm text-ink-soft">Searching…</div>}
          {!loading && !results.length && <div className="px-4 py-3 text-sm text-ink-soft">No students match "{q.trim()}"</div>}
          {results.map((s, i) => (
            <button key={s.id} onMouseDown={e => e.preventDefault()} onClick={() => pick(s)}
              onMouseEnter={() => setCursor(i)}
              className={`w-full text-left px-4 py-2.5 flex items-center justify-between gap-3 border-t border-line-soft first:border-t-0
                ${i === cursor ? 'bg-transit-tint' : ''}`}>
              <span className="min-w-0">
                <span className="block font-semibold text-sm text-ink truncate">{s.name}</span>
                <span className="block text-xs text-ink-soft truncate">
                  {s.guardian_name ? `${s.guardian_name} · ${s.guardian_phone}` : 'No guardian linked'}
                </span>
              </span>
              <span className="text-xs font-bold text-transit-deep shrink-0">
                {s.class_name || '—'}{s.roll_number ? ` · #${s.roll_number}` : ''}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
